
import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Trash2, UserCog, Shield, Check, X, UserIcon, Star, EyeIcon, Building } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { 
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { User } from "./types";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";

interface UserTableProps {
  users: User[];
  loading: boolean;
  handleToggleAdminRole: (userId: string, isCurrentlyAdmin: boolean) => Promise<void>;
  handleDeleteUser: (userId: string) => Promise<void>;
  handleToggleUserActive?: (userId: string, currentlyActive: boolean) => Promise<void>;
  handleChangeUserRole?: (userId: string, newRole: string) => Promise<void>;
  canChangeRoles?: boolean;
  extraActions?: (user: User) => React.ReactNode;
}

const UserTable: React.FC<UserTableProps> = ({
  users,
  loading,
  handleToggleAdminRole,
  handleDeleteUser,
  handleToggleUserActive,
  handleChangeUserRole,
  canChangeRoles = false,
  extraActions,
}) => {
  const { user: currentUser } = useAuth();
  const { direction } = useLanguage();
  const isRtl = direction === "rtl";
  
  const getRoleBadgeColor = (role?: string) => {
    switch(role) {
      case 'super': return "bg-amber-500 hover:bg-amber-600 text-white";
      case 'admin': return "bg-blue-500 hover:bg-blue-600 text-white";
      case 'member': return "bg-green-500 hover:bg-green-600 text-white";
      case 'viewer': return "bg-gray-500 hover:bg-gray-600 text-white";
      default: return "bg-gray-200 hover:bg-gray-300";
    }
  };
  
  const getRoleIcon = (role?: string) => {
    switch(role) {
      case 'super': return <Star className="h-3 w-3 mr-1" />;
      case 'admin': return <Shield className="h-3 w-3 mr-1" />;
      case 'member': return <UserIcon className="h-3 w-3 mr-1" />;
      case 'viewer': return <EyeIcon className="h-3 w-3 mr-1" />;
      default: return null;
    }
  };
  
  const getSubscriptionBadge = (status?: string) => {
    if (!status) return null;
    
    switch(status) {
      case 'active':
        return <Badge className="bg-green-500">מנוי פעיל</Badge>;
      case 'trial':
        return <Badge className="bg-blue-500">תקופת ניסיון</Badge>;
      case 'expired':
        return <Badge className="bg-red-500">פג תוקף</Badge>;
      case 'free':
      default:
        return <Badge className="bg-gray-500">חינם</Badge>;
    }
  };
  
  const getPlanBadge = (plan?: string) => {
    if (!plan) return null;
    
    switch(plan) {
      case 'professional':
        return <Badge className="bg-purple-500">מקצועי</Badge>;
      case 'premium':
        return <Badge className="bg-amber-500">פרימיום</Badge>;
      case 'basic':
      default:
        return <Badge className="bg-gray-500">בסיסי</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="text-center py-10">
        <div className="w-10 h-10 border-4 border-synagogue-blue border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p>{isRtl ? "טוען משתמשים..." : "Loading users..."}</p>
      </div>
    );
  }

  console.log("Rendering user table with users:", users);

  return (
    <div className="border rounded-md overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{isRtl ? "דוא״ל" : "Email"}</TableHead>
            <TableHead>{isRtl ? "תאריך יצירה" : "Created At"}</TableHead>
            <TableHead>{isRtl ? "תפקיד" : "Role"}</TableHead>
            <TableHead>{isRtl ? "סטטוס" : "Status"}</TableHead>
            <TableHead>{isRtl ? "מנוי" : "Subscription"}</TableHead>
            <TableHead>{isRtl ? "תוכנית" : "Plan"}</TableHead>
            <TableHead className="w-[180px]">{isRtl ? "פעולות" : "Actions"}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-6">
                {isRtl ? "אין משתמשים רשומים" : "No users registered"}
              </TableCell>
            </TableRow>
          ) : (
            users.map((user) => (
              <TableRow key={user.id} className={!user.is_active ? "bg-gray-100" : ""}>
                <TableCell>
                  <span className="font-medium">{user.email}</span>
                  {user.created_by && (
                    <div className="text-xs text-gray-500 flex items-center mt-1">
                      <Building className="h-3 w-3 mr-1" />
                      <span>{isRtl ? "הוזמן ע״י:" : "Invited by:"} {user.created_by === currentUser?.id ? (isRtl ? "אתה" : "You") : user.created_by.substring(0, 8)}</span>
                    </div>
                  )}
                </TableCell>
                <TableCell>{user.created_at}</TableCell>
                <TableCell>
                  {canChangeRoles && handleChangeUserRole ? (
                    <Select 
                      defaultValue={user.role || 'member'}
                      onValueChange={(value) => handleChangeUserRole(user.id, value)}
                      disabled={user.id === currentUser?.id} // Can't change own role
                    >
                      <SelectTrigger className={`w-[130px] ${getRoleBadgeColor(user.role)}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectLabel>{isRtl ? "תפקידים" : "Roles"}</SelectLabel>
                          <SelectItem value="super">{isRtl ? "מנהל-על" : "Super Admin"}</SelectItem>
                          <SelectItem value="admin">{isRtl ? "מנהל" : "Admin"}</SelectItem>
                          <SelectItem value="member">{isRtl ? "משתמש" : "Member"}</SelectItem>
                          <SelectItem value="viewer">{isRtl ? "צפייה בלבד" : "Viewer"}</SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge className={getRoleBadgeColor(user.role)}>
                      {getRoleIcon(user.role)}
                      {user.role || (isRtl ? 'משתמש' : 'User')}
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  {handleToggleUserActive ? (
                    <div className="flex items-center">
                      <Switch
                        checked={user.is_active}
                        onCheckedChange={() => handleToggleUserActive(user.id, user.is_active || false)}
                        disabled={user.id === currentUser?.id} // Can't deactivate self
                      />
                      <span className={isRtl ? "mr-2" : "ml-2"}>{user.is_active ? (isRtl ? 'פעיל' : 'Active') : (isRtl ? 'לא פעיל' : 'Inactive')}</span>
                    </div>
                  ) : (
                    <span className="flex items-center">
                      {user.is_active ? 
                        <Check className="h-4 w-4 text-green-500 mr-2" /> : 
                        <X className="h-4 w-4 text-red-500 mr-2" />
                      }
                      {user.is_active ? (isRtl ? 'פעיל' : 'Active') : (isRtl ? 'לא פעיל' : 'Inactive')}
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  {getSubscriptionBadge(user.subscription_status)}
                </TableCell>
                <TableCell>
                  {getPlanBadge(user.plan_tier)}
                </TableCell>
                <TableCell>
                  <div className={`flex ${isRtl ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleAdminRole(user.id, user.role === 'admin')}
                      title={user.role === 'admin' ? (isRtl ? "הסר הרשאות מנהל" : "Remove admin rights") : (isRtl ? "הענק הרשאות מנהל" : "Grant admin rights")}
                    >
                      <UserCog className={`h-4 w-4 ${user.role === 'admin' ? 'text-synagogue-accent' : 'text-gray-500'}`} />
                      <span className="sr-only">
                        {user.role === 'admin' ? (isRtl ? "הסר הרשאות מנהל" : "Remove admin rights") : (isRtl ? "הענק הרשאות מנהל" : "Grant admin rights")}
                      </span>
                    </Button>
                    {extraActions && extraActions(user)}
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteUser(user.id)}
                      disabled={user.id === currentUser?.id} // Can't delete self
                      title={isRtl ? "מחק משתמש" : "Delete user"}
                      className="bg-red-500 hover:bg-red-600"
                    >
                      <Trash2 className="h-4 w-4 text-white" />
                      <span className={isRtl ? "mr-1" : "ml-1"}>
                        {isRtl ? "מחק" : "Delete"}
                      </span>
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default UserTable;
